/**
 * Partnerbutikker: register a shop, say what members get there, hand its
 * phone a link, switch it off, read what it scanned.  CA-128.
 *
 * The link carries the shop's key in the #fragment and is shown ONCE — the
 * server keeps only a hash. Switching a shop off is two clicks. While
 * scanning is off (MASJID_PARTNER_SCAN, until CA-94) the page says so: shops can
 * be registered but cannot scan.
 *
 * MEDLEMSFORDELER (2026-09-20). The offer — "10 % på alt unntatt tobakk" —
 * had no field anywhere, so the app could tell members that places give them
 * a discount and never which places. Two things follow from adding it:
 *
 *   * **"Vises i appen" is a separate switch from being registered.** A shop
 *     is registered when the agreement is signed and its till can scan; the
 *     offer is published when somebody has written it down. They are not the
 *     same day, and defaulting to published would have put every existing
 *     partner into the app with an empty line under its name.
 *   * **The offer is edited in place.** Before this, changing 10 % to 15 %
 *     meant deactivating the shop and registering it again, which issues a
 *     new key and breaks the till mid-week.
 *
 * What a member never sees: the contact person and the scanning key. That is
 * enforced in core (partner-scan.memberView), not here — this page shows
 * `inApp`, which is that same function's answer.
 */
import React, { useState } from 'react';
import { API, call, errText, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { osloTime } from '../lib/format';
import { Body as CardBody, Btn, Card, CopyField, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { PictureField, sendPicture, type Picked } from '../components/Picture';

/** Owner 2026-10-25: a shop's picture is its card in the app (Medlemsfordeler and Hjem). */
const PICTURE_NOTE = 'Vises som et kort i appen, under Medlemsfordeler og på Hjem. Helst liggende (16:9): butikken, varene eller tilbudet. Ikke bilder av personer uten at de har sagt ja. JPG, PNG eller WebP.';

const SCAN_RESULT_NO: Record<string, string> = { valid: 'Gyldig', not_valid: 'Ikke gyldig', code_expired: 'Utløpt kode', unreadable: 'Kunne ikke leses' };
/** The kinds, in the app's reading order. The codes come from core. */
const CATEGORY_NO: Record<string, string> = {
  food: 'Mat og dagligvarer', clothing: 'Klær', health: 'Helse',
  home: 'Hjem og bolig', services: 'Tjenester', other: 'Annet',
};
const scanUrl = (link: string) => new URL(link, new URL(API).origin).toString();
const when = (iso?: string | null) => iso ? osloTime(iso) : '—';

const EMPTY = { name: '', contact: '', benefit_no: '', category: '', address: '', url: '', visible: false };
type Form = typeof EMPTY;

/** The offer fields, shared by "register" and "edit" so the two cannot drift. */
function OfferFields({ f, set, categories }: { f: Form; set: (f: Form) => void; categories: string[] }) {
  return (
    <>
      <div className="row">
        <Field label="Hva får medlemmet?" className="field"
               note="Skriv kort og konkret, slik det skal stå i appen — for eksempel «10 % på alt unntatt tobakk». Teksten oversettes automatisk til engelsk og urdu.">
          {(id) => <input id={id} maxLength={160} autoComplete="off" value={f.benefit_no}
                          onChange={(e) => set({ ...f, benefit_no: e.target.value })} />}
        </Field>
      </div>
      <div className="row">
        <Field label="Kategori" className="field half">
          {(id) => (
            <select id={id} value={f.category} onChange={(e) => set({ ...f, category: e.target.value })}>
              <option value="">— velg —</option>
              {categories.map((c) => <option key={c} value={c}>{CATEGORY_NO[c] || c}</option>)}
            </select>
          )}
        </Field>
        <Field label="Adresse" className="field half">
          {(id) => <input id={id} maxLength={160} autoComplete="off" value={f.address}
                          onChange={(e) => set({ ...f, address: e.target.value })} />}
        </Field>
      </div>
      <div className="row">
        <Field label="Nettsted" className="field half" note="Må begynne med https://">
          {(id) => <input id={id} maxLength={200} autoComplete="off" placeholder="https://" value={f.url}
                          onChange={(e) => set({ ...f, url: e.target.value })} />}
        </Field>
        <Field label="Vises i appen" className="field half"
               note="Av til noen har skrevet hva medlemmet får. Butikken kan skanne uansett.">
          {(id) => (
            <label htmlFor={id} className="field note" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id={id} type="checkbox" checked={f.visible}
                     onChange={(e) => set({ ...f, visible: e.target.checked })} />
              Vis under Medlemsfordeler
            </label>
          )}
        </Field>
      </div>
    </>
  );
}

export function Partners() {
  const { data, error, reload } = useLoad<Body>('/partners');
  const [link, setLink] = useState<string | null>(null);
  const [msg, say] = useMsg();
  const [f, setF] = useState<Form>(EMPTY);
  const [edit, setEdit] = useState<{ id: string; f: Form; image: string | null } | null>(null);
  const [pic, setPic] = useState<Picked>(null);
  const [editPic, setEditPic] = useState<Picked>(null);
  const [log, setLog] = useState<{ name: string; items: Body[] } | null>(null);
  // Owner 2026-10-23: «Registrer en butikk» opens in a popup; the scanning link it gives stays on the page afterwards.
  const [adding, setAdding] = useState(false);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  const categories: string[] = data.categories || Object.keys(CATEGORY_NO);
  const showLink = (l: string, text: string) => { setLink(scanUrl(l)); say(text, 'ok'); };
  return (
    <>
      <Card title="Partnerbutikker med medlemsfordeler">
        {!data.enabled && <CardBody><p className="msg err" role="status" style={{ marginTop: 0 }}>Skanning er ikke slått på ennå. Du kan registrere butikker, men de kan ikke skanne før personvernerklæringen er godkjent og funksjonen er slått på.</p></CardBody>}
        <table className="t">
          <thead><tr><th scope="col">Butikk</th><th scope="col">Fordel i appen</th><th scope="col">Kontakt</th><th scope="col">Skanninger siste 7 dager</th><th scope="col">Sist skannet</th><th scope="col">Status</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>{items.length ? items.map((p) => (
            <tr key={p.id}>
              <td>{p.image && <img src={p.image.url} alt="" style={{ width: 96, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8, display: 'block', marginBottom: 6 }} />}
                <b>{p.name}</b>{p.category && <div className="sub">{CATEGORY_NO[p.category] || p.category}</div>}</td>
              <td>{p.benefit_no || <span className="sub">Ikke skrevet ennå</span>}
                <div>{p.inApp ? <Tag tone="green">Vises i appen</Tag> : <Tag tone="grey">Skjult</Tag>}</div></td>
              <td>{p.contact || '—'}</td><td>{p.scans7d}</td><td>{when(p.lastScanAt)}</td>
              <td>{p.active ? <Tag tone="green">Aktiv</Tag> : <Tag tone="grey">Slått av {when(p.deactivatedAt)}</Tag>}</td>
              <td><Btn ghost aria-label={`Skannelogg for ${p.name}`} onClick={async () => {
                  // A refused or failed read is said (it showed nothing at all, which reads as "no scans").
                  try { const r = await call(`/partners/${p.id}/scans`); setLog({ name: p.name, items: r.items || [] }); }
                  catch (x) { setLog(null); say('Skanneloggen kunne ikke hentes: ' + (x as Error).message + ' Prøv igjen.', 'err'); }
                }}>Skannelogg</Btn>
                {p.active && <>{' '}<Btn ghost onClick={() => { setEdit({ id: p.id, f: {
                    name: p.name || '', contact: p.contact || '', benefit_no: p.benefit_no || '',
                    category: p.category || '', address: p.address || '', url: p.url || '', visible: !!p.visible }, image: p.image ? p.image.url : null }); setEditPic(null); say('', 'ok'); }}>Endre fordel</Btn>{' '}
                  {/* QA A13: a new link retires the old one at once — the shop's till stops scanning until it opens the new one. */}
                  <TwoStep label="Ny lenke" ariaLabel={`Ny skannelenke for ${p.name}`} busyLabel="Lager lenke …"
                    confirmLabel="Klikk igjen: den gamle lenken slutter å virke med en gang" onConfirm={async () => {
                      const r = await post(`/partners/${p.id}/key`); if (r.ok) showLink(r.body.scanLink, r.body.message_no); else say(errText(r.body), 'err'); }} />{' '}
                  <TwoStep label="Slå av" ariaLabel={`Slå av ${p.name}`} confirmLabel="Klikk igjen: butikken kan ikke skanne lenger" onConfirm={async () => { const r = await post(`/partners/${p.id}/deactivate`); say(r.ok ? r.body.message_no : errText(r.body), r.ok ? 'ok' : 'err'); reload(); }} /></>}</td>
            </tr>)) : <tr><td colSpan={7}>Ingen partnerbutikker ennå.</td></tr>}</tbody>
        </table>
        <CardBody>
          <div className="new-row"><Btn small={false} onClick={() => { say(''); setAdding(true); }}>+ Registrer en butikk</Btn></div>
          {!adding && <Msg text={msg.text} tone={msg.tone} />}
          {link && <CopyField label="Åpne denne lenken på butikkens telefon. Den vises bare nå." value={link} />}

          {/* Editing an existing offer. The scanning key is untouched here —
              a shop's till keeps working while the discount changes. */}
          {edit && <>
            <h3 style={{ marginTop: 22 }}>Endre fordel — {edit.f.name}</h3>
            <OfferFields f={edit.f} set={(nf) => setEdit({ ...edit, f: nf })} categories={categories} />
            <PictureField label="Bilde i appen" note={PICTURE_NOTE} current={edit.image} picked={editPic} onPick={setEditPic} />
            <div className="row">
              <Btn onClick={async () => {
                const r = await patch(`/partners/${edit.id}`, edit.f);
                if (!r.ok) return say(r.body.message || errText(r.body), 'err');
                const bad = await sendPicture(`/partners/${edit.id}/image`, editPic);
                say(bad ? `${r.body.message_no} Men bildet ble ikke lagret: ${bad}` : r.body.message_no, bad ? 'err' : 'ok');
                setEdit(null); setEditPic(null); reload();
              }}>Lagre</Btn>{' '}
              <Btn ghost onClick={() => setEdit(null)}>Avbryt</Btn>
            </div>
          </>}

          <FormDialog open={adding} title="Registrer en butikk" dirty={JSON.stringify(f) !== JSON.stringify(EMPTY) || pic !== null}
            onClose={() => { setAdding(false); setF(EMPTY); setPic(null); }}>
          <div className="row">
            <Field label="Navn på butikken" required className="field half">{(id) => <input id={id} maxLength={80} autoComplete="off" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
            <Field label="Kontaktperson eller telefon" className="field half" note="Til moskeens eget bruk. Vises aldri i appen.">{(id) => <input id={id} maxLength={120} autoComplete="off" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />}</Field>
          </div>
          <OfferFields f={f} set={setF} categories={categories} />
          <PictureField label="Bilde i appen" note={PICTURE_NOTE} picked={pic} onPick={setPic} />
          <div className="row"><Btn onClick={async () => {
            setLink(null);
            const r = await post('/partners', f);
            if (!r.ok) return say(r.body.message || errText(r.body), 'err');
            // The shop exists now, so its picture has somewhere to go. A refused picture leaves the shop registered.
            const bad = await sendPicture(`/partners/${r.body.partner.id}/image`, pic);
            setF(EMPTY); setPic(null); setAdding(false);
            showLink(r.body.scanLink, bad ? `${r.body.message_no} Bildet ble ikke lagret: ${bad} Legg det inn under «Endre fordel».` : r.body.message_no); reload();
          }}>Registrer butikk</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
          </FormDialog>
          {log && <>
            <h3 style={{ marginTop: 22 }}>Skannelogg — {log.name}</h3>
            <table className="t"><thead><tr><th scope="col">Tid</th><th scope="col">Resultat</th><th scope="col">Medlemsnr.</th></tr></thead>
              <tbody>{log.items.length ? log.items.map((i, k) => <tr key={k}><td>{when(i.at)}</td>
                <td>{SCAN_RESULT_NO[i.result] || i.result}{i.repeat && <span className="sub"> (skannet to ganger)</span>}</td><td>{i.memberNo || '—'}</td></tr>)
                : <tr><td colSpan={3}>Ingen skanninger ennå.</td></tr>}</tbody></table>
          </>}
        </CardBody>
      </Card>
    </>
  );
}
